import {
  Text,
  Card,
  CardHeader,
  Badge,
  makeStyles,
  tokens,
} from '@fluentui/react-components';

const useStyles = makeStyles({
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: tokens.spacingHorizontalL,
    marginTop: tokens.spacingVerticalL,
  },
  card: { padding: tokens.spacingVerticalM },
  metric: { fontSize: '32px', fontWeight: 700, color: tokens.colorBrandForeground1 },
});

interface Metric {
  label: string;
  value: string;
  hint: string;
}

/**
 * Admin > Reports. A reporting shell (usage / satisfaction) shown to users with Reports.View. The
 * figures are placeholders — this is the surface the reporting API/queries will populate.
 */
export function ReportsPage({ variant }: { variant: 'usage' | 'csat' }) {
  const styles = useStyles();
  const config = variant === 'csat' ? CSAT : USAGE;

  return (
    <section>
      <Text as="h1" size={600} weight="semibold" style={{ display: 'block' }}>{config.title}</Text>
      <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>{config.subtitle}</Text>
      <Badge appearance="outline" color="warning" style={{ marginLeft: 8 }}>Sample data</Badge>

      <div className={styles.cards}>
        {config.metrics.map((m) => (
          <Card key={m.label} className={styles.card}>
            <CardHeader header={<Text weight="semibold">{m.label}</Text>} />
            <div className={styles.metric}>{m.value}</div>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{m.hint}</Text>
          </Card>
        ))}
      </div>
    </section>
  );
}

const USAGE = {
  title: 'Usage Report',
  subtitle: 'Conversation volume and engagement across the support portal.',
  metrics: [
    { label: 'Conversations (7d)', value: '1,284', hint: '+12% vs prior week' },
    { label: 'Active users (7d)', value: '742', hint: '+5% vs prior week' },
    { label: 'Avg. turns / chat', value: '6.3', hint: 'Stable' },
    { label: 'Handoffs to agent', value: '184', hint: '14% of conversations' },
  ] satisfies Metric[],
};

const CSAT = {
  title: 'Satisfaction Report',
  subtitle: 'Customer satisfaction signals from post-chat surveys and thumbs feedback.',
  metrics: [
    { label: 'Avg. CSAT', value: '4.4 / 5', hint: 'Across 512 responses' },
    { label: 'Thumbs up', value: '3,910', hint: '88% positive' },
    { label: 'Thumbs down', value: '523', hint: '12% negative' },
    { label: 'Response rate', value: '39%', hint: '+3% vs prior month' },
  ] satisfies Metric[],
};
